import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

/**
 * Suivi des absences (CDC §6.6), écran `ped-absences`. Tout vient de l'API :
 * état de chaque journée, aperçu du mail, cumul, palier du courrier et refus.
 * L'écran n'en recalcule rien.
 */

export type TypeAbsence = 'MATIN' | 'APRES_MIDI' | 'JOURNEE_COMPLETE';
export type PeriodeAbsences = 'MOIS' | 'TRIMESTRE' | 'TOUT';
export type EtatInformation = 'ENVOYEE' | 'A_INFORMER' | 'SANS_ENTREPRISE' | 'SANS_ADRESSE' | 'PROVISOIRE';

export interface MotifJustification {
  code: string;
  libelle: string;
  precisionExigee: boolean;
}

export interface MessageAbsence {
  titre: string;
  corps: string;
}

export interface CumulAbsences {
  total: number;
  injustifiees: number;
  justifiees: number;
  seuil: number;
  seuilDepasse: boolean;
  margeAvantSeuil: number;
  palier: number;
}

export interface DemiJourneeAbsente {
  presenceId: string;
  demiJournee: 'MATIN' | 'APRES_MIDI';
  demiJourneeLibelle: string;
  seance: string;
  horaires: string;
  provisoire: boolean;
  justifiee: boolean;
  motifJustification: string | null;
  justificatifEnAttente: boolean;
  information: EtatInformation;
  informationLibelle: string;
  informationTon: string;
  entrepriseNotifieeLe: string | null;
  entrepriseNotifieeA: string | null;
  apercu: MessageAbsence | null;
  refusInformation: string | null;
}

export interface LigneJournee {
  cle: string;
  apprenantId: string;
  apprenant: string;
  mineur: boolean;
  formation: string | null;
  siteCode: string;
  jour: string;
  jourLisible: string;
  type: TypeAbsence;
  typeLibelle: string;
  typeTon: string;
  provisoire: boolean;
  demiJournees: DemiJourneeAbsente[];
  justification: { etat: 'JUSTIFIEE' | 'PARTIELLE' | 'EN_EXAMEN' | 'NON'; libelle: string };
  information: { etat: EtatInformation; libelle: string; ton: string; le: string | null };
  entreprise: { denomination: string; adresses: string[] } | null;
  cumul: CumulAbsences;
  avertissement: {
    palierDu: number | null;
    dernier: { palier: number; le: string; documentId: string | null } | null;
  };
}

export interface JustificatifAExaminer {
  id: string;
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  motifLibelle: string;
  nomFichier: string;
  typeMime: string;
  deposeLe: string;
  demiJournees: string[];
}

export interface ReponseAbsences {
  periode: PeriodeAbsences;
  journees: LigneJournee[];
  justificatifs: JustificatifAExaminer[];
  seuils: { formationId: string; code: string; titre: string; seuil: number }[];
  compteurs: {
    absencesDuJour: number;
    duJour: { matin: number; apresMidi: number; journee: number };
    mailsEnvoyesDuJour: number;
    aInformer: number;
    seuilAtteint: number;
    courriersDus: number;
    justificatifsAExaminer: number;
    tauxPresence: number | null;
  };
  automatismes: { information: boolean; avertissement: boolean };
  expedition: EtatExpedition;
  seancesOuvertes: number;
  motifs: MotifJustification[];
  seuilParDefaut: number;
}

export function useAbsences(site: string, periode: PeriodeAbsences, actif = true) {
  const parametres = new URLSearchParams({ site, periode });
  return useQuery({
    queryKey: ['absences', parametres.toString()],
    queryFn: () => appelApi<ReponseAbsences>(`/absences?${parametres.toString()}`),
    enabled: actif,
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['absences'] });
  void client.invalidateQueries({ queryKey: ['emargement'] });
  void client.invalidateQueries({ queryKey: ['assiduite'] });
  void client.invalidateQueries({ queryKey: ['absenteisme'] });
  void client.invalidateQueries({ queryKey: ['apprenants-risque'] });
}

export function useJustifierAbsence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      presenceId: string;
      justifiee: boolean;
      motif?: string;
      precision?: string;
    }) => {
      const { presenceId, ...corps } = variables;
      return appelApi<{ message: string }>(`/absences/${presenceId}`, { methode: 'PATCH', corps });
    },
    onSuccess: () => invalider(client),
  });
}

/** Envoi, ou renvoi, du mail d'une demi-journée à l'employeur. */
export function useInformerEntreprise() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (presenceId: string) =>
      appelApi<{ message: string }>(`/absences/presences/${presenceId}/information`, {
        methode: 'POST',
      }),
    onSuccess: () => invalider(client),
  });
}

export function useGenererAvertissement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (apprenantId: string) =>
      appelApi<{ message: string }>(`/absences/apprenants/${apprenantId}/avertissement`, {
        methode: 'POST',
      }),
    onSuccess: () => invalider(client),
  });
}

/** « Générer les courriers (n) » : tous les paliers dus du périmètre. */
export function useGenererAvertissementsDus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (site: string) =>
      appelApi<{ produits: number; message: string }>('/absences/avertissements', {
        methode: 'POST',
        corps: site ? { site } : {},
      }),
    onSuccess: () => invalider(client),
  });
}

export function useDeciderJustificatif() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; decision: 'ACCEPTER' | 'REFUSER'; motifRefus?: string }) => {
      const { id, ...corps } = variables;
      return appelApi<{ message: string }>(`/absences/justificatifs/${id}/decision`, {
        methode: 'POST',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

/** Fichier d'un justificatif, servi par une route gardée et tracée. */
export function urlJustificatifAbsence(id: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/absences/justificatifs/${encodeURIComponent(id)}/fichier`;
}

/** Courrier d’avertissement, lu depuis l’écran des absences (route gardée, tracée). */
export function urlCourrierAvertissement(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/absences/courriers/${encodeURIComponent(documentId)}/fichier`;
}
